import { Box, Button, Grid, Typography } from "@mui/material";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useProductsContext } from "../../Contexts/SidebarContext";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil, readJson } from "../../utils/CommonUtil";
import ToastManager from "../../Components/ToastManager";
import { useNavigate } from "react-router-dom";
import SearchBarWithButton from "../BroadcastFBManage/DMComponents/SearchBarWithButton";
import AudienceList from "../BroadcastFBManage/DMComponents/AudienceList";
import { CreateCustomAudienceDialog } from "./CreateCADialog";

interface Audience {
  customAudienceId: number;
  customAudienceName: string;
  customAudienceDesc: string;
  audienceSize: number;
  syncStatus: string;
}

const CustomAudiencePage: React.FC = () => {
  const navigate = useNavigate();
  const { sideBar } = useProductsContext();

  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [audience, setAudience] = useState<Audience[]>([]);

  const {
    facebook: { pageId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId } = readJson("fbbAdAccountDetail") || "{}";

  const filteredAudiences = useMemo(
    () =>
      audience.filter((a) =>
        a.customAudienceName?.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [audience, searchTerm],
  );

  const onSuccessCall = useCallback((data: string) => {
    const respData = CommonUtil.parseData(data);
    if (respData) {
      if (respData.statusCode === "BM0000" && respData.caDetailsList) {
        setAudience(respData.caDetailsList);
      } else {
        ToastManager.showToast("No custom audiences are available.", "Error");
      }
    }
  }, []);

  const onErrorCall = useCallback(() => {
    ToastManager.showToast("Failed to fetch custom audiences.", "Error");
  }, []);

  const fetchCustomAudiences = useCallback(() => {
    if (!pageId || !adAccountId) {
      return;
    }
    const pageDataReq = `pageId=${pageId}&adAccountId=${adAccountId}`;
    try {
      NetworkManager.sendJsonRequest(
        "broadcastCustomAudiences/getCustomAudiencesDetails",
        "",
        pageDataReq,
        "GET",
        true,
        onSuccessCall,
        onErrorCall,
      );
    } catch (error) {
      ToastManager.showToast("Error fetching custom audiences.", "Error");
    }
  }, [pageId, adAccountId, onSuccessCall, onErrorCall]);

  useEffect(() => {
    fetchCustomAudiences();
  }, [fetchCustomAudiences]);

  const handleCreate = useCallback(() => {
    setOpenDialog(true);
  }, []);

  const handleBackToPages = useCallback(() => {
    navigate("/existingbroadcastpages");
  }, [navigate]);

  // ✅ Shared centering style — mirrors old md={10} lg={8} Grid breakpoints.
  // mx:"auto" centres the content on every viewport with no hardcoded offsets.
  const centeredContentSx = {
    width: "100%",
    maxWidth: { xs: "100%", md: "83.33%", lg: "66.66%" },
    mx: "auto",
  };

  return (
    <Box
      className="content-page"
      sx={{
        ml: sideBar ? "250px" : "120px",
        backgroundColor: "#fff",
        mr: 10,
      }}>
      <Box className="content">
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* ── Row 1: Back button + Title ── */}
          <Grid container spacing={2} sx={{ alignItems: "center", mt: 0 }}>
            <Grid size={4} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                sx={{ textTransform: "none", ml: 20 }}
                onClick={handleBackToPages}>
                ← Back To Pages
              </Button>
            </Grid>
            <Grid size={8} sx={{ mt: 2 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: "bold", textAlign: "start", ml: 14 }}>
                Custom Audiences
              </Typography>
            </Grid>
          </Grid>

          {/* ── Row 2: Search bar + Create button ── */}
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <Box sx={centeredContentSx}>
              <SearchBarWithButton
                placeholder="Search Custom Audience..."
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onNew={handleCreate}
                newBtnLabel="+ Create Custom Audience"
              />
            </Box>
          </Box>

          {/* ── Row 3: Audience list (scrollable) ── */}
          <Box
            className="custom-scrollbar-css"
            sx={{
              height: "60vh",
              overflowY: "auto",
              display: "flex",
              justifyContent: "center",
            }}>
            <Box sx={centeredContentSx}>
              <AudienceList audiences={filteredAudiences} />
            </Box>
          </Box>
        </Box>

        <CreateCustomAudienceDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSave={handleCreate}
          fetchCustomAudiences={fetchCustomAudiences}
        />
      </Box>
    </Box>
  );
};
export default CustomAudiencePage;
